import { createRouter, createWebHistory } from "vue-router";
import { omit as _omit } from "lodash-es";
import { PATHS } from "@/router/paths";
import useAnalyticsUtils from "@/shared/composables/analytics/use-analytics-utils";
import { ngLogoutUnauthorized, useAuthStore } from "@/shared/stores/use-auth-store";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import { useSeo } from "@/shared/composables/use-seo";
import { useHeader } from "@/shared/stores/use-header";
import { waitUntilTrue } from "@/shared/utils/wait";
import { useUiStateStore } from "@/shared/composables/use-ui-state";

const LiveView = () => import("@/live/views/LiveView.vue");
const NotFound = () => import("@/shared/views/NotFound.vue");
const LiveEventDetail = () => import("@/live/views/LiveEventDetail.vue");
const PrematchView = () => import("@/prematch/views/PrematchView.vue");
const PrematchDetailView = () => import("@/prematch/views/PrematchDetailView.vue");
const TicketView = () => import("@/ticket/views/TicketView.vue");
const Homepage = () => import("@/homepage/views/Homepage.vue");
const MyTicketsView = () => import("@/my-tickets/views/MyTicketsView.vue");
const TicketSettingsView = () => import("@/ticket/views/TicketSettingsView.vue");
const SearchView = () => import("@/search/views/SearchView.vue");
const StatsView = () => import("@/third-party/views/StatsView.vue");
const DentsuView = () => import("@/third-party/views/DentsuView.vue");
const { setPageAnalytics } = useAnalyticsUtils();
const { isLandscape, isMobile } = useIsBreakpoint();

const removeActiveTabQueryParam = (to) => {
    if (to.query.activeTab) {
        window.query = { ...window?.query, activeTab: to.query.activeTab.toString() };
        return { ...to, query: _omit(to.query, "activeTab") };
    }
};

const router = createRouter({
    history: createWebHistory(window.BASE_PATH),
    routes: [
        {
            path: PATHS.HOME,
            name: "Homepage",
            component: Homepage,
        },
        {
            path: PATHS.LIVE,
            name: "Live",
            component: LiveView,
        },
        {
            path: PATHS.PREMATCH,
            name: "Prematch",
            component: PrematchView,
        },
        {
            path: `${PATHS.LIVE}/:id`,
            name: "LiveEventDetail",
            component: LiveEventDetail,
            props: true,
        },
        {
            path: `${PATHS.PREMATCH}/:sportAndCountryId`,
            name: "PrematchSport",
            component: PrematchView,
            beforeEnter: [removeActiveTabQueryParam],
        },
        {
            path: `${PATHS.PREMATCH}/:sportAndCountryId/:leagueId`,
            name: "PrematchLeagueDetail",
            component: PrematchDetailView,
            beforeEnter: [removeActiveTabQueryParam],
        },
        {
            path: `${PATHS.PREMATCH}/:sportAndCountryId/:leagueId/:eventId`,
            name: "PrematchEventDetail",
            component: PrematchDetailView,
            beforeEnter: [removeActiveTabQueryParam],
        },
        {
            path: PATHS.TICKET,
            name: "Ticket",
            component: TicketView,
            beforeEnter: [removeActiveTabQueryParam],
        },
        {
            path: PATHS.TICKET_SETTINGS,
            name: "TicketSettings",
            component: TicketSettingsView,
        },
        {
            path: PATHS.STATS,
            name: "Stats",
            component: StatsView,
        },
        {
            path: PATHS.MY_TICKETS,
            name: "MyTickets",
            component: MyTicketsView,
            meta: { requiresAuth: true, requiresObAuth: true },
            beforeEnter: [removeActiveTabQueryParam],
        },
        {
            path: PATHS.SEARCH,
            name: "Search",
            component: SearchView,
        },
        {
            path: "/:pathMatch(.*)*",
            component: NotFound,
        },
    ],
    async scrollBehavior(to, from, savedPosition) {
        if (to.path === from.path) return;

        try {
            if (isMobile.value) {
                if (savedPosition) {
                    const uiStateStore = useUiStateStore();
                    await waitUntilTrue(() => uiStateStore.uiState === "idle", 100, 1500);
                    return { top: savedPosition.top };
                }
                // If there is no savedPosition - cases when you user goes to specific sport/league/event by URL and uses backlink button
                // We use the approach of identifying the missing parameter and scrolling to the element
                else if (["Prematch", "PrematchSport", "PrematchLeagueDetail", "Live"].includes(to.name)) {
                    const { checkRemovedParamsAndScrollToElement } = useHeader();
                    await checkRemovedParamsAndScrollToElement(to, from);
                } else {
                    return { top: 0 };
                }
            } else {
                // Do not scroll to top on event details
                if (["PrematchEventDetail"].includes(to.name)) return;

                return { top: 0 };
            }
        } catch (error) {
            console.error("Router timeout or error:", error);
        }
    },
});

//prevent access to prematch view on desktop (when clearing filters or refreshing)
router.beforeEach((to, from, next) => {
    const authStore = useAuthStore();
    const isMobileResolution = window.innerWidth <= 768;

    if (to.name == "LiveEventDetail") {
        if (to.path !== from.path) {
            window.scrollTo(0, 0);
        }
    }

    if (to.name === "Prematch" && !isMobileResolution && !isLandscape.value) {
        next({ name: "Homepage" });
    } else if (
        (to.meta?.requiresAuth && !authStore.isTokenAvailable) ||
        (to.meta?.requiresObAuth && !authStore.isWsTokenAvailable)
    ) {
        next({ name: "Homepage" });
    } else {
        next();
    }

    // hide footer on stats page
    if (to.name === "Stats") {
        document.body.classList.add("footer-hidden");
    }

    if (from != null && from.name === "Stats") {
        document.body.classList.remove("footer-hidden");
    }

    //automatically change analytics page
    setPageAnalytics(to);
});

// Apply seo meta tags on route change
router.afterEach((to, from) => {
    if (to.matched[0].components == DentsuView) {
        useSeo().forDentsuPath(to.path);
    } else {
        useSeo().forPath(to.path);
    }
});

export default router;
